import type { ReactNode } from 'react';
import type { DistanceLocale } from '@wineryxplorer/shared';
import { CompactVenueCard } from './CompactVenueCard';
import type { CompactVenueCardProps } from './CompactVenueCard';
import { MobileBottomSheet } from './MobileBottomSheet';

interface MobileMapOverlayProps {
  map: ReactNode;
  onClose: () => void;
  venues: CompactVenueCardProps['venue'][] | undefined;
  highlightedVenueId: string | null;
  userLocation: { lat: number; lng: number } | null;
  distanceLocale: DistanceLocale;
  isPending: boolean;
}

/**
 * Full-screen map overlay shown on narrow screens, with the venue cards in a
 * bottom sheet over the map.
 */
export const MobileMapOverlay = ({
  map,
  onClose,
  venues,
  highlightedVenueId,
  userLocation,
  distanceLocale,
  isPending,
}: MobileMapOverlayProps) => (
  <div className="fixed inset-0 z-50 lg:hidden">
    {/* Map fills the screen */}
    <div className="absolute inset-0">{map}</div>

    {/* Close / back-to-list button */}
    <button
      type="button"
      onClick={onClose}
      className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg shadow-md hover:bg-gray-50 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
      List
    </button>

    {/* Bottom sheet with venue cards */}
    <MobileBottomSheet>
      <div className="space-y-3">
        {venues?.map((venue) => (
          <div
            key={venue._id}
            className={`rounded-lg transition-shadow ${
              highlightedVenueId === venue._id ? 'shadow-lg' : ''
            }`}
          >
            <CompactVenueCard
              venue={venue}
              userLocation={userLocation}
              distanceLocale={distanceLocale}
            />
          </div>
        ))}
        {(!venues || venues.length === 0) && (
          <p className="text-sm text-gray-500 text-center py-4">
            {isPending ? 'Searching...' : 'No venues found in this area'}
          </p>
        )}
      </div>
    </MobileBottomSheet>
  </div>
);
